<template>
  <v-dialog v-model="dialog" max-width="800px" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">{{ titulo }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="cerrarDialog">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="pago">
        <v-row>
          <!-- Información del participante -->
          <v-col cols="12">
            <h3>Información del Participante</h3>
            <v-divider class="mb-3"></v-divider>
            
            <div v-if="pago.personas && pago.personas[0]">
              <p><strong>Nombre:</strong> {{ pago.personas[0].nombre }} {{ pago.personas[0].apellido }}</p>
              <p><strong>Documento:</strong> {{ pago.personas[0].documento }}</p>
            </div>
            
            <p><strong>Modalidad:</strong> {{ pago.modalidad && pago.modalidad.nombre ? pago.modalidad.nombre : 'N/A' }}</p>
            <p><strong>Estado:</strong> 
              <v-chip :color="getEstadoColor(pago.state)" text-color="white" small>
                {{ getEstadoLabel(pago.state) }}
              </v-chip>
            </p>
          </v-col>

          <!-- Información del comprobante -->
          <v-col cols="12">
            <h3>Comprobante de Pago</h3>
            <v-divider class="mb-3"></v-divider>
            
            <v-row>
              <v-col cols="12" sm="6">
                <p><strong>Fecha de subida:</strong> {{ formatearFecha(pago.payment_receipt_uploaded_at) }}</p>
                <p><strong>Tamaño:</strong> {{ formatFileSize(pago.payment_receipt_size) }}</p>
                <p><strong>Tipo:</strong> {{ getFileExtension(pago.payment_receipt_mime) }}</p>
              </v-col>
              
              <v-col cols="12" sm="6">
                <v-btn
                  v-if="pago.payment_receipt_url"
                  color="primary"
                  block
                  @click="descargarComprobante(pago)"
                  :loading="descargando"
                >
                  <v-icon left>cloud_download</v-icon>
                  Descargar Comprobante
                </v-btn>
              </v-col>
            </v-row>

            <div v-if="pago.payment_receipt_url" class="mt-4">
              <h4>Vista Previa</h4>
              <v-divider class="mb-2"></v-divider>
              <v-img
                v-if="esImagen(pago.payment_receipt_mime)"
                :src="pago.payment_receipt_url"
                max-width="400"
                contain
                class="elevation-2"
              />

              <iframe
                v-else-if="esPdf(pago.payment_receipt_mime)"
                :src="pago.payment_receipt_url"
                style="width:100%;height:400px;border:1px solid #ccc;border-radius:4px;"
              />

              <v-alert v-else type="info" outlined>
                No se puede previsualizar este tipo de archivo.
              </v-alert>
            </div>
          </v-col>

          <!-- Cambiar estado (solo si es modo editar) -->
          <v-col cols="12" v-if="modo === 'editar'">
            <h3>Cambiar Estado</h3>
            <v-divider class="mb-3"></v-divider>
            
            <v-select
              v-model="nuevoEstado"
              :items="estadosDisponibles"
              item-text="text"
              item-value="value"
              label="Nuevo Estado"
              outlined
            />
            
            <v-textarea
              v-model="observaciones"
              label="Observaciones (opcional)"
              outlined
              rows="3"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cerrarDialog">Cancelar</v-btn>
        <v-btn
          v-if="modo === 'editar'"
          color="primary"
          :loading="guardando"
          @click="guardarCambios"
        >
          Guardar Cambios
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api/inscripcion';

export default {
  name: 'PagoDialog',
  data() {
    return {
      dialog: false,
      pago: null,
      modo: 'ver',
      nuevoEstado: '',
      observaciones: '',
      guardando: false,
      estadosDisponibles: [
        { text: 'Aprobado', value: 'pago_realizado' },
        { text: 'En Revisión', value: 'pago_en_revision' },
        { text: 'Rechazado', value: 'pago_rechazado' },
        { text: 'Pendiente', value: 'pago_pendiente' }
      ]
    };
  },
  computed: {
    titulo() {
      return this.modo === 'editar' ? 'Editar Pago' : 'Ver Pago';
    }
  },
  methods: {
    abrirDialog(pago, modo = 'ver') {
      this.pago = { ...pago };
      this.modo = modo;
      this.nuevoEstado = pago.state;
      this.observaciones = '';
      this.dialog = true;
    },

    cerrarDialog() {
      this.dialog = false;
      this.pago = null;
      this.modo = 'ver';
      this.nuevoEstado = '';
      this.observaciones = '';
    },

    async guardarCambios() {
      if (!this.nuevoEstado) return;

      this.guardando = true;
      
      try {
        await api.cambiarEstadoInscripcion(this.pago.id, {
          state: this.nuevoEstado,
          observaciones: this.observaciones
        });

        this.$store.commit('app/SET_MESSAGE', 'Estado actualizado correctamente');
        this.$store.commit('app/SET_SHOW', true);
        
        this.$emit('actualizar');
        this.cerrarDialog();
        
      } catch (error) {
        console.error('Error actualizando estado:', error);
        this.$store.commit('app/SET_MESSAGE', 'Error al actualizar el estado');
        this.$store.commit('app/SET_SHOW', true);
      } finally {
        this.guardando = false;
      }
    },

    getEstadoColor(estado) {
      const colores = {
        'pago_realizado': 'success',
        'pago_en_revision': 'warning',
        'pago_rechazado': 'error',
        'pago_pendiente': 'info'
      };
      return colores[estado] || 'grey';
    },

    getEstadoLabel(estado) {
      const labels = {
        'pago_realizado': 'Aprobado',
        'pago_en_revision': 'En Revisión',
        'pago_rechazado': 'Rechazado',
        'pago_pendiente': 'Pendiente'
      };
      return labels[estado] || estado;
    },

    formatearFecha(fecha) {
      return fecha ? this.$moment(fecha).format('DD/MM/YYYY HH:mm') : 'N/A';
    },

    getFileExtension(mime) {
      const extensions = {
        'application/pdf': 'PDF',
        'image/jpeg': 'JPG',
        'image/png': 'PNG',
        'image/jpg': 'JPG'
      };
      return extensions[mime] || 'Archivo';
    },

    formatFileSize(bytes) {
      if (!bytes) return 'N/A';
      return (bytes / 1024).toFixed(1) + ' KB';
    },

    esImagen(mime) {
      return mime && mime.startsWith('image/');
    },

    esPdf(mime) {
      return mime === 'application/pdf';
    }
  }
};
</script>